import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Phone, Globe, Clock, QrCode, Check, MessageSquare, Flame } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo, { localBusinessJsonLd, breadcrumbJsonLd, speakableJsonLd } from "@/components/Seo";
import { PhoneLink, AddressLink } from "@/components/ContactLinks";
import ShareMenu from "@/components/ShareMenu";
import { resolveImgSrc } from "@/components/ImageUpload";

export default function BusinessProfile({ checkinMode = false }) {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [qr, setQr] = useState(null);
  const [boost, setBoost] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/businesses/${id}`).then(r => setData(r.data));
    api.get(`/qr/${id}`).then(r => setQr(r.data));
    api.get(`/streak-boosts/active`).then(r => {
      const active = (r.data.boosts || []).find(b => b.business_id === id);
      setBoost(active || null);
    }).catch(() => {});
  }, [id]);

  const handleCheckIn = async () => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    const doCheckin = async (lat, lon) => {
      try {
        const { data: res } = await api.post("/checkins", { business_id: id, latitude: lat, longitude: lon });
        if (res.qualifies_for_grand_prize) {
          toast.success("Grand Prize eligible! Check-in confirmed.");
        } else {
          toast.success(`Checked in at ${res.business_name}. Stamp added.`);
        }
        nav("/dashboard/passport");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Check-in failed");
      } finally { setBusy(false); }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => doCheckin(pos.coords.latitude, pos.coords.longitude),
        () => doCheckin(null, null),
        { enableHighAccuracy: true, timeout: 6000 }
      );
    } else {
      doCheckin(null, null);
    }
  };

  if (!data) return <div className="max-w-6xl mx-auto p-8 text-white/60">Loading…</div>;
  const b = data.business;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <Seo
        title={`${b.business_name} — Jacksonville`}
        description={`${b.business_name} in Jacksonville, FL. ${b.description || ""} Official ADN Local FanFest ${b.watch_party_partner ? "Watch Party Partner" : "listed"} location.`.slice(0, 180)}
        image={(b.photos && b.photos[0]) || undefined}
        type="business.business"
        jsonLd={[
          localBusinessJsonLd(b),
          breadcrumbJsonLd([
            { name: "Home", url: "/" },
            { name: "Directory", url: "/directory" },
            { name: b.business_name, url: `/business/${b.id}` },
          ]),
          speakableJsonLd([".speakable-title", ".speakable-summary"]),
        ]}
      />
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 adn-card overflow-hidden">
          <div className="h-64 sm:h-80 relative">
            <img src={resolveImgSrc(b.photos && b.photos[0]) || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200"} className="w-full h-full object-cover"/>
            <div className="absolute top-3 left-3 flex gap-2">
              {b.sponsor_tier && b.sponsor_tier !== "free" && (
                <span className="px-2 py-1 rounded-full bg-primary/90 text-black text-[10px] tracking-widest uppercase">{b.sponsor_tier}</span>
              )}
              {b.watch_party_partner && (
                <span className="px-2 py-1 rounded-full bg-secondary text-black text-[10px] tracking-widest uppercase">Watch Party</span>
              )}
            </div>
          </div>
          <div className="p-6 sm:p-8">
            {boost && (
              <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 border border-orange-500/50 rounded-full text-sm"
                data-testid="business-boost-badge">
                <Flame size={14} className="text-orange-400"/>
                <span className="text-orange-300 font-bold">{boost.multiplier}X STREAK TODAY</span>
                <span className="text-white/60 text-xs">— check in here counts as {boost.multiplier} days</span>
              </div>
            )}
            <h1 className="font-bebas text-4xl sm:text-5xl">{b.business_name}</h1>
            <div className="mt-2 text-white/70 text-sm space-y-1">
              <div className="flex items-center gap-2"><MapPin size={14}/>
                <AddressLink address={b.address} city={b.city} state={b.state} zip_code={b.zip_code}
                  data-testid="business-address-link">
                  {b.address} · {b.city}, {b.state}
                </AddressLink>
              </div>
              {b.phone && <div className="flex items-center gap-2"><Phone size={14}/><PhoneLink phone={b.phone} data-testid="business-phone-link"/></div>}
              {b.website && <div className="flex items-center gap-2"><Globe size={14}/>{b.website}</div>}
              {b.hours && <div className="flex items-center gap-2"><Clock size={14}/>{b.hours}</div>}
            </div>
            <p className="mt-4 text-white/80 leading-relaxed">{b.description}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={handleCheckIn} disabled={busy}
                data-testid="checkin-button"
                className="inline-flex items-center gap-2 px-6 py-4 bg-primary text-black font-bebas text-2xl rounded-sm hover:brightness-110 disabled:opacity-60 min-h-[56px]">
                <Check size={18}/> {busy ? "CHECKING IN…" : "CHECK IN NOW"}
              </button>
              <ShareMenu
                url={typeof window !== "undefined" ? window.location.href : ""}
                title={b.business_name}
                text={`Check out ${b.business_name} on ADN Local FanFest`}/>
              {user?.role === "consumer" && (
                <button onClick={async () => {
                  try {
                    const { data } = await api.post("/chat/conversations",
                      { kind: "business", business_id: id });
                    nav(`/dashboard/messages?c=${data.id}`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "You need to check in here first before messaging.");
                  }
                }}
                  data-testid="message-business-button"
                  className="inline-flex items-center gap-2 px-6 py-4 border border-white/25 rounded-sm hover:border-primary min-h-[56px]">
                  <MessageSquare size={16}/> Message
                </button>
              )}
            </div>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="adn-card p-6">
            <div className="text-xs tracking-widest uppercase text-primary flex items-center gap-2"><QrCode size={14}/> Check-in QR</div>
            {qr && <img src={qr.png_base64} className="mx-auto my-4 w-40 h-40 bg-white p-2 rounded" alt="QR"/>}
            <div className="text-[11px] text-white/50 text-center">Scan on-site with any camera to check in.</div>
          </div>
          <div className="adn-card p-6">
            <div className="text-xs tracking-widest uppercase text-primary mb-2">Current Offers</div>
            {data.offers.length === 0 && <div className="text-white/60 text-sm">No active offers.</div>}
            <ul className="space-y-3">
              {data.offers.map(o => (
                <li key={o.id} className="border border-adn-border rounded p-3">
                  <div className="font-semibold">{o.title}</div>
                  <div className="text-xs text-white/60">{o.description}</div>
                </li>
              ))}
            </ul>
          </div>
          {data.events.length > 0 && (
            <div className="adn-card p-6">
              <div className="text-xs tracking-widest uppercase text-primary mb-2">Events</div>
              <ul className="space-y-2">
                {data.events.map(e => (
                  <li key={e.id} className="text-sm">
                    <div className="font-semibold">{e.title}</div>
                    <div className="text-xs text-white/60">{e.event_date} · {e.start_time}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
